/**
 * Visibility rules for qualification fields. Both keys are optional extras on the
 * qualification_fields JSON — fields without them behave exactly as before.
 *
 *   patient_types: ['new_patient']            → only shown to that patient type
 *   visible_if: { field_id, values: [...] }   → only shown once the referenced
 *                                               field's answer matches one of values
 */

export function fieldMatchesPatientType(field, patientSelected) {
    const types = field?.patient_types;
    if (!Array.isArray(types) || types.length === 0) return true;
    if (!patientSelected || patientSelected === 'all' || patientSelected === 'new_and_returning_patient') return true;
    return types.includes(patientSelected);
}

export function filterFieldsForPatientType(fields, patientSelected) {
    if (!Array.isArray(fields)) return fields;
    return fields.filter((f) => fieldMatchesPatientType(f, patientSelected));
}

/**
 * Optional per-procedure gating: a field carrying `procedures: [...]` (procedure
 * names and/or ids) is only shown when the visitor's selected procedure matches.
 * Fields without the key show for every procedure, exactly as before. A gated
 * field stays hidden when no procedure is known (e.g. the visitor typed a
 * free-text service) — a hidden field is never submitted, so the backend
 * disqualifier re-evaluation skips it too.
 */
export function fieldMatchesProcedures(field, selectedTokens) {
    const wanted = field?.procedures;
    if (!Array.isArray(wanted) || wanted.length === 0) return true;
    if (!Array.isArray(selectedTokens) || selectedTokens.length === 0) return false;
    const norm = (v) => String(v).trim().toLowerCase();
    const selected = selectedTokens.filter((v) => v !== null && v !== undefined && String(v).trim() !== '').map(norm);
    if (selected.length === 0) return false;
    return wanted.some((w) => selected.includes(norm(w)));
}

export function filterFieldsForProcedures(fields, selectedTokens) {
    if (!Array.isArray(fields)) return fields;
    return fields.filter((f) => fieldMatchesProcedures(f, selectedTokens));
}

/**
 * Consult-request custom fields render inline on the request form when there
 * are ≤2 of them, EXCEPT when any field carries a disqualifier: qualification
 * gates must run one-per-page after submit so the client-side
 * step/disqualified fires before the consult conversion is dispatched.
 */
export function shouldStepConsultFields(fields) {
    if (!Array.isArray(fields) || fields.length === 0) return false;
    return fields.length > 2 || fields.some((f) => f && f.disqualifier);
}

/**
 * @param {Object} field - qualification field, possibly carrying visible_if
 * @param {Object} answers - map of field id -> answer (listDataQuestion)
 * @returns {boolean} true when the field should be shown
 */
export function conditionSatisfied(field, answers) {
    const cond = field?.visible_if;
    if (!cond || cond.field_id == null || !Array.isArray(cond.values) || cond.values.length === 0) return true;

    const answer = answers?.[cond.field_id] ?? answers?.[String(cond.field_id)];
    if (answer === undefined || answer === null) return false;

    const wanted = cond.values.map((v) => String(v).trim().toLowerCase());

    // multi_select answers are stored as a JSON array string — match on any overlap
    if (typeof answer === 'string' && answer.startsWith('[') && answer.endsWith(']')) {
        try {
            const arr = JSON.parse(answer);
            if (Array.isArray(arr)) {
                return arr.some((v) => wanted.includes(String(v).trim().toLowerCase()));
            }
        } catch (_) { /* fall through to plain string match */ }
    }

    // checkbox answers are stored as 1/0 — normalize to yes/no
    let norm;
    if (answer === 1 || answer === '1') norm = 'yes';
    else if (answer === 0 || answer === '0') norm = 'no';
    else norm = String(answer).trim().toLowerCase();

    return wanted.includes(norm);
}
